import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'

type StripDocument = { id: string; title: string; kind: string; thumbnail: ReactNode }

export function DocumentStrip({ label, documents, selected, onSelect, previousLabel, nextLabel }: {
  label: string; documents: StripDocument[]; selected: string | null; onSelect: (id: string) => void
  previousLabel: string; nextLabel: string
}) {
  const id = useId()
  const listRef = useRef<HTMLUListElement>(null)
  const [edges, setEdges] = useState({ previous: false, next: false })
  function updateEdges() {
    const list = listRef.current
    if (list) setEdges({ previous: list.scrollLeft > 1, next: list.scrollLeft + list.clientWidth < list.scrollWidth - 1 })
  }
  useEffect(() => {
    updateEdges()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateEdges)
    if (listRef.current) observer?.observe(listRef.current)
    return () => observer?.disconnect()
  }, [documents])
  function scroll(direction: number) {
    const list = listRef.current
    if (list) list.scrollBy({ left: direction * list.clientWidth * 0.8 })
  }
  return <div className="document-strip-group">
    <ul ref={listRef} id={id} className="document-strip" aria-label={label} onScroll={updateEdges}>
    {documents.map(document => <li key={document.id}>
      <button type="button" className="document-strip__item" aria-pressed={selected === document.id}
        onFocus={event => event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' })}
        onClick={() => onSelect(document.id)} title={`${document.title} · ${document.kind}`}>
        <span className="document-strip__thumbnail" aria-hidden="true">{document.thumbnail}</span>
        <span className="document-strip__title">{document.title}</span>
        <span className="document-strip__kind">{document.kind}</span>
      </button>
    </li>)}
    </ul>
    {(edges.previous || edges.next) && <div className="document-strip__navigation">
      <Button icon="chevronL" iconOnly aria-label={previousLabel} title={previousLabel} aria-controls={id}
        disabled={!edges.previous} onClick={() => scroll(-1)} />
      <Button icon="chevronR" iconOnly aria-label={nextLabel} title={nextLabel} aria-controls={id}
        disabled={!edges.next} onClick={() => scroll(1)} />
    </div>}
  </div>
}
